import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { ProductServices } from "../../../core/services/ProductServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import {
  ProductFields,
  ProductListParams,
  Permission,
} from "./ProductDataTypes";
import "./Products.css";
import ProductFilters from "./ProductFilters";
import {
  getLabelFor,
  isEmptyVal,
  isNotEmpty,
  processMinute,
} from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import DescriptionCell from "../../components/Elements/DescriptionCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import { displayField } from "../../../utils/helpers";
import useDecryptedParams from "../../../navigation/useDecryptParams";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
  roleTypeArr,
  productNatureObjArr,
  productGenderObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import EditCell from "../../components/Elements/EditCell";
import ProductSingleAction from "./ProductSingleAction";
import StoreProductBulkImportAction from "./StoreProductBulkImportAction";
import CompanyProductSingleAction from "./CompanyProductSingleAction";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => true;
}

function ImportProductsForStore() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ProductFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const modalRef = useRef<ImageModal>(null);
  const [selectedProduct, setSelectedProductData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const params: any = useParams();
  let [decryptedParams] = useDecryptedParams(params);

  const initSearchFormData = {
    product_type: "",
    product_category: "",
    product_name: "",
    product_sku: "",
    tentative_price: "",
    original_price: "",
    min_qty: "",
    hygenic_index: "",
    process_time: "",
    food_type: "",
    size: "",
    available_on_day: "",
    product_cost_percent: "",
    product_cost_percent_op: "gte",
    product_for_gender: "",
    product_nature: "",
    status: "",
    smart_search: "",
  };

  const permissionObj: Permission = {
    publish: false,
    update: false,
    delete: false,
  };

  const [formData, setFormData] =
    useState<ProductListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };
  function getStoreId() {
    let sId = null;
    if (
      UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client &&
      !isEmptyVal(UtilityServices.getLoggedInUser("store_id"))
    ) {
      sId = UtilityServices.getLoggedInUser("store_id");
    } else {
      sId = decryptedParams.id;
    }
    return sId;
  }
  async function getProducts(params: ProductListParams) {
    try {
      setLoading(true);
      const res = await ProductServices.getStoreProductsToImport(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
      navigate(ContentRoutes.STORES);
    }
  }

  async function init() {
    try {
      getStoreId() &&
        getProducts(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, [decryptedParams.id]);

  async function bulkImportProducts(dataToImport: any) {
    try {
      let params = {
        store_id: getStoreId(),
        products: JSON.stringify(dataToImport),
      };
      let res = await ProductServices.importProductsForStore(params);
      setPage(1);
      getProducts(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function onDeleteProduct() {
    try {
      setPage(1);
      getProducts(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    } catch (error: any) {
      console.log(error);
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function productPicModal(value: any) {
    if (
      !failedImages.includes(value.large_image_url) &&
      value.large_image_url != null &&
      value.large_image_url !== ""
    ) {
      setSelectedProductData(value);
      modalRef.current?.openModal();
      modalRef.current?.hideDelete();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: ProductListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    params.for_store = getStoreId();
    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getProducts(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getProducts(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getProducts(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getProducts(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterProducts = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getProducts(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getProducts(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getProducts(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const handleCellChange = (idv: any, key: any, value: any) => {
    const newData: any[] = [];
    data.forEach(function (item: any) {
      if (item.id === idv) {
        item[key] = value;
      }
      newData.push(item);
    });
    setData(newData);
  };

  return (
    <div className="products">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4 className="mb-2">{t("PRODUCTS")}</h4>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Header>
        <Content>
          <ProductFilters onSubmit={submitSearch} onClear={clearSearch} />
          <div className="row">
            <StoreProductBulkImportAction
              storeId={getStoreId()}
              checkedKeys={checkedKeys}
              setCheckedKeys={setCheckedKeys}
              onBulkImport={bulkImportProducts}
              onDeleteProduct={onDeleteProduct}
              data={data}
            />
            <div className="col-md-4">
              <InputGroup>
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("NAME").toLowerCase() +
                      " " +
                      t("OR").toLowerCase() +
                      " " +
                      t("SKU").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterProducts(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterProducts(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>

          <div className={total > 0 ? "usr-tbl-border" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              <Column width={50} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={80} align="center" fixed>
                <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
                <ImageCellFormatter
                  dataKey="large_image_url"
                  rowData={(rowData: any) => rowData}
                  dummyImage={loggedInUserDetail.product_default_image}
                  onFailedImages={getFailedImages}
                  openPictureModal={productPicModal}
                />
              </Column>
              <Column width={120} sortable resizable fixed>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="product_name">
                  {(rowData: any) => (
                    <span
                      title={
                        (isNotEmpty(`${rowData.product_sku}`) &&
                          `${rowData.product_sku}`) ||
                        `${rowData.product_name}`
                      }
                    >
                      {displayField(rowData.product_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>

              <Column width={120} sortable fixed>
                <CustomHeaderCell>{t("CATEGORY")}</CustomHeaderCell>
                <CustomCell dataKey="cat_name" />
              </Column>
              <Column width={100}>
                <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                <CustomCell dataKey="product_nature">
                  {(rowData: any) => (
                    <span>
                      {" "}
                      {displayField(
                        getLabelFor(
                          rowData?.product_nature,
                          productNatureObjArr
                        )
                      )}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100}>
                <CustomHeaderCell>
                  {t("FOR")} {t("GENDER")}
                </CustomHeaderCell>
                <CustomCell dataKey="product_for_gender">
                  {(rowData: any) => (
                    <span>
                      {" "}
                      {displayField(
                        getLabelFor(
                          rowData?.product_for_gender,
                          productGenderObjArr
                        )
                      )}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={110}>
                <CustomHeaderCell>
                  {t("ORIGINAL")} {t("PRICE")}
                </CustomHeaderCell>
                <EditCell
                  rowData={(rowData: any) => rowData}
                  dataKey="company_product_original_price"
                  onChange={handleCellChange}
                />
              </Column>
              <Column width={80}>
                <CustomHeaderCell>
                  {t("FINAL")} {t("PRICE")}
                </CustomHeaderCell>
                <EditCell
                  rowData={(rowData: any) => rowData}
                  dataKey="company_product_price"
                  onChange={handleCellChange}
                />
              </Column>
              <Column width={70}>
                <CustomHeaderCell>{t("COST")}</CustomHeaderCell>
                <EditCell
                  rowData={(rowData: any) => rowData}
                  dataKey="product_cost"
                  onChange={handleCellChange}
                />
              </Column>
              <Column width={100} resizable>
                <CustomHeaderCell>
                  {t("COST")} {t("DETAIL")}
                </CustomHeaderCell>
                <EditCell
                  rowData={(rowData: any) => rowData}
                  dataKey="product_cost_detail"
                  onChange={handleCellChange}
                />
              </Column>
              <Column width={120} resizable>
                <CustomHeaderCell>
                  {t("COMPETITOR")} {t("PRICE")}
                </CustomHeaderCell>
                <EditCell
                  rowData={(rowData: any) => rowData}
                  dataKey="competitor_price"
                  onChange={handleCellChange}
                />
              </Column>
              <Column width={100}>
                <CustomHeaderCell>
                  {t("SERVICE")} {t("TIME")}
                </CustomHeaderCell>
                <CustomCell dataKey="process_time">
                  {(rowData: any) => (
                    <span>{processMinute(rowData.process_time)}</span>
                  )}
                </CustomCell>
              </Column>
              <Column width={200} resizable>
                <CustomHeaderCell>{t("DESCRIPTION")}</CustomHeaderCell>
                <DescriptionCell
                  rowData={(rowData: any) => rowData}
                  dataIndex={"product_detail"}
                  introLength={200}
                />
              </Column>
              <Column width={120} resizable>
                <CustomHeaderCell>
                  {t("CARE")} {t("DETAIL")}
                </CustomHeaderCell>
                <DescriptionCell
                  rowData={(rowData: any) => rowData}
                  dataIndex={"care_detail"}
                />
              </Column>
              <Column width={120} resizable>
                <CustomHeaderCell>
                  {t("SPECIAL")} {t("NOTE")}
                </CustomHeaderCell>
                <DescriptionCell
                  rowData={(rowData: any) => rowData}
                  dataIndex={"special_note"}
                />
              </Column>
              <Column width={120} resizable>
                <CustomHeaderCell>
                  {t("VIDEO")} {t("URL")}
                </CustomHeaderCell>
                <CustomCell dataKey="product_video_url">
                  {(rowData: any) => (
                    <span title={displayField(`${rowData.product_video_url}`)}>
                      {displayField(rowData.product_video_url)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={60} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <CompanyProductSingleAction
                      productData={rowData}
                      permission={permissionObj}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={t("PRODUCT") + " " + t("IMAGE")}
        heading={t("PRODUCT") + " " + t("IMAGE")}
        body={
          <img
            src={selectedProduct.large_image_url}
            className="main-profile-pic"
            alt={t("PROFILE_PIC")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
    </div>
  );
}

export default ImportProductsForStore;
